/**
 * Front Desk exit re-auth wrapper (dink-api `/me/verify-password`, DNKADM-0031).
 *
 * `verifyPassword(password)` POSTs the signed-in admin's OWN password to dink-api,
 * which takes the caller's email FROM THE JWT (no email is sent; a sent one is
 * ignored) and forwards the check server-to-server to saas-auth. **It issues NO
 * token/session/cookie — it only answers true/false.** Used solely to gate exiting
 * Front Desk view back to the full console.
 *
 * Contract (DNKAPI-0083, ASSUMED — dink-api's parallel track; MSW-tested here):
 *  - `POST /me/verify-password` `{ password }`, the member JWT (the same bearer the
 *    app already holds), bearer-only via `request()` on the `/me/**` surface (NO
 *    CSRF, NO cookies; the bounded 401→refresh→retry applies).
 *  - `200 { valid: true }`  → password matches → exit Front Desk.
 *  - `200 { valid: false }` → doesn't match (indistinguishable wrong/no-password —
 *    no enumeration) → inline "incorrect"; stay. **NEVER a 401**, so it can never
 *    trip the bearer 401→refresh→sign-out path.
 *  - `400` → blank/missing password (the dialog blocks the empty submit first).
 *  - `401` → no valid JWT (session gone — NOT a wrong password) → Log out escape.
 *  - `429` → throttled → cool-down (disable submit until it clears).
 *  - `502`/`500` → server/config fault → FAIL CLOSED (stay locked; generic message).
 *
 * **The password is never stored, cached, or logged** — it lives only in the
 * request body for the duration of the call. The defensive parse FAILS CLOSED: a
 * missing / wrong-typed `valid` is surfaced as a fail-closed fault, never a silent
 * pass.
 */
import { request } from './client';
import { ApiError } from './errors';

/**
 * The classified outcome of an exit re-auth attempt. `valid` is the only path that
 * exits Front Desk view; every other outcome keeps it locked.
 *
 *  - `valid`        — `200 { valid: true }`: exit to the full view.
 *  - `invalid`      — `200 { valid: false }`: wrong/blank password; inline retry, stay.
 *  - `blank`        — `400`: empty password reached the server (dialog blocks first).
 *  - `throttled`    — `429`: cool-down; disable submit until it clears.
 *  - `sessionGone`  — `401`: the JWT is gone; drop to the Log out escape.
 *  - `failClosed`   — `500`/`502`/network/malformed body: couldn't verify; stay locked,
 *                     generic message (NOT "wrong password").
 */
export type VerifyPasswordResult =
  | { kind: 'valid' }
  | { kind: 'invalid' }
  | { kind: 'blank' }
  | { kind: 'throttled' }
  | { kind: 'sessionGone' }
  | { kind: 'failClosed' };

/**
 * Verify the signed-in admin's OWN password to gate exiting Front Desk view.
 * Resolves to a {@link VerifyPasswordResult} for every outcome — it does NOT throw;
 * the caller switches on `kind`. The password is sent only in the request body and
 * is never stored or logged. The parse fails closed: only an explicit
 * `{ valid: true }` returns `valid`; a `{ valid: false }` returns `invalid`; any
 * other body shape returns `failClosed` (never a silent pass).
 */
export async function verifyPassword(password: string): Promise<VerifyPasswordResult> {
  try {
    const body = await request<unknown>('POST', '/me/verify-password', {
      json: { password },
    });
    if (typeof body === 'object' && body !== null && 'valid' in body) {
      const valid = (body as { valid: unknown }).valid;
      if (valid === true) return { kind: 'valid' };
      if (valid === false) return { kind: 'invalid' };
    }
    // Missing / wrong-typed `valid` → fail closed (never a silent pass).
    return { kind: 'failClosed' };
  } catch (err) {
    if (err instanceof ApiError) {
      switch (err.status) {
        case 400:
          return { kind: 'blank' };
        case 401:
          return { kind: 'sessionGone' };
        case 429:
          return { kind: 'throttled' };
        default:
          // 500/502/other server fault, or a network error → fail closed.
          return { kind: 'failClosed' };
      }
    }
    return { kind: 'failClosed' };
  }
}
